import { erp, founders, cta, type Status } from '../content';
import { Arrow, Button, SmartLink, StatusChip, Swipe, usePageMeta } from '../ui';
import { ErpSyncExplainer } from '../explainers';
import { CtaBand, Faq, KitStyles, PageHero, Section, TwoTone, breadcrumbJsonLd, useJsonLd } from './platform/kit';

// How each ERP connects, in one short phrase (conceptual; no mappings or implementation detail).
const HOW: Record<string, string> = {
  'Epicor Kinetic': 'Native connector',
  'IFS Cloud': 'Our API, with your partner',
  'SAP S/4HANA & Business One': 'Our API, with your partner',
  'Anything else': 'CSV, REST API, webhooks',
};

const OTHER: { t: string; d: string; status: Status }[] = [
  { t: 'REST API and webhooks', d: 'Scoped API keys, and signed events for registrations, handovers and new cases.', status: 'live' },
  { t: 'Spreadsheet import and export', d: 'Products, serials and owners in and out by CSV.', status: 'live' },
  { t: 'Payments and carriers', d: 'Paid into your payment account, shipped on your carrier accounts. You are the merchant of record.', status: 'live' },
  { t: 'Messaging (your own account)', d: 'SMS and messages to owners through your own provider.', status: 'live' },
  { t: 'MCP for AI agents', d: 'Your own AI tools work with your records, within the scopes you grant.', status: 'pilot' },
];

// What an IT team asks, in one list (one place, not a grid plus an FAQ).
const FAQS = [
  { q: 'Which ERP systems does BrandedMark integrate with?', a: 'Epicor Kinetic has a native connector. IFS Cloud and SAP S/4HANA or Business One connect through our REST API and webhooks, usually with your integration partner; native connectors are on our roadmap. Anything else can start with a spreadsheet.' },
  { q: 'Do we have to change how we work in the ERP?', a: 'No. Parts, serials and customers stay mastered in your ERP. We read them, and approved returns can go back as RMAs. Your team keeps its screens.' },
  { q: 'What do we have to install?', a: 'No server and no middleware. For Epicor Kinetic, the connector runs inside Kinetic as a function library and saved queries, on Kinetic\'s own scheduler. Other systems call our API.' },
  { q: 'Who can reach our data?', a: 'Only the scopes you grant. API keys are stored only as hashes, and you can rotate or revoke them. Webhooks are signed. Security questionnaires and an architecture summary under NDA.' },
  { q: 'How do you handle GDPR?', a: 'A data processing agreement in our terms, an appointed EU representative, and every sub-processor published in the legal hub.' },
  { q: 'Do you support single sign-on?', a: 'Not today. Staff sign in with two-factor or passkeys, which your admins can make mandatory.' },
  { q: 'What if we leave?', a: 'Export everything at any time, free, with no switching charges. Your codes keep working.' },
];

function ErpBoard() {
  return (
    <div className="pk-card overflow-hidden">
      <div className="flex items-center justify-between border-b border-bm-line px-5 py-4 sm:px-6">
        <div>
          <div className="text-[15px] font-semibold text-bm-indigo">Which ERP do you run?</div>
        </div>
        <span className="font-mono text-[11.5px] text-bm-muted">Oct 2026</span>
      </div>
      <ul>
        {erp.systems.map((s, i) => (
          <li key={s.name} className="pk-rise border-b border-bm-line last:border-b-0" style={{ animationDelay: `${0.2 + i * 0.12}s` }}>
            {s.href ? (
              <SmartLink href={s.href} className="group flex items-center justify-between gap-4 px-5 py-4 transition-colors hover:bg-bm-alt sm:px-6">
                <div className="min-w-0">
                  <div className="text-[16.5px] font-semibold text-bm-indigo">{s.name}</div>
                  <div className="mt-0.5 text-[13.5px] text-bm-muted">{HOW[s.name]}</div>
                  <div className="mt-1.5 inline-flex items-center gap-1.5 text-[13.5px] font-semibold text-bm-emerald">
                    What it reads, writes and needs <Arrow />
                  </div>
                </div>
                <span className="grid h-10 w-10 flex-none place-items-center rounded-full text-bm-indigo shadow-[inset_0_0_0_1.5px_var(--bm-line)] transition-colors group-hover:bg-bm-indigo group-hover:text-white group-hover:shadow-none" aria-hidden="true">
                  <Arrow />
                </span>
              </SmartLink>
            ) : (
              <div className="flex items-center justify-between gap-4 px-5 py-4 sm:px-6">
                <div className="min-w-0">
                  <div className="text-[16.5px] font-semibold text-bm-indigo">{s.name}</div>
                  <div className="mt-0.5 text-[13.5px] text-bm-muted">{HOW[s.name]}</div>
                </div>
                <StatusChip status={s.status} />
              </div>
            )}
          </li>
        ))}
      </ul>
      <div className="flex items-center gap-2 border-t border-bm-line bg-bm-alt px-5 py-3 text-[12.5px] text-bm-muted sm:px-6">
        Every system connects through the API today.
      </div>
    </div>
  );
}

function Flows() {
  return (
    <div className="mx-auto max-w-4xl" data-reveal>
      <ErpSyncExplainer />
    </div>
  );
}

function Proof() {
  return (
    <div className="grid gap-10 lg:grid-cols-[1fr_1fr] lg:items-center lg:gap-16">
      <div className="frame" data-reveal>
        <img src="/images/v2/gen/precision-packing.jpg" alt="Gloved hands packing machined aluminium parts into a foam-lined crate, paperwork tucked in the lid" width={1536} height={1024} loading="lazy" className="aspect-[3/2] w-full object-cover" />
      </div>
      <div data-reveal style={{ ['--reveal-delay' as string]: '0.08s' }}>
        <div className="it-big">
          About <Swipe>four weeks</Swipe>
        </div>
        <p className="mt-6 text-[clamp(19px,1.8vw,23px)] font-semibold leading-snug tracking-[-0.015em] text-bm-indigo">From kick-off to scannable packing slips, straight from the ERP. Our first customer starts there.</p>
        <dl className="mt-8 grid grid-cols-2 gap-px overflow-hidden rounded-[16px] border border-bm-line bg-bm-line">
          {[
            ['Who', 'A leading European manufacturer'],
            ['Starts with', 'Packing slips'],
          ].map(([k, v]) => (
            <div key={k} className="bg-white px-4 py-3">
              <dt className="text-[13.5px] font-medium text-bm-muted">{k}</dt>
              <dd className="mt-0.5 text-[15px] font-semibold text-bm-indigo">{v}</dd>
            </div>
          ))}
        </dl>
        <div className="mt-6">
          <SmartLink href="/customers" className="pk-textlink">
            Read the customer story <Arrow />
          </SmartLink>
        </div>
      </div>
    </div>
  );
}

function Team() {
  return (
    <div className="grid gap-12 lg:grid-cols-[1fr_1.1fr] lg:gap-16">
      <div data-reveal>
        <h2 className="t-h2">
          {founders.title[0]} <Swipe>{founders.title[1]}</Swipe>
        </h2>
        <p className="t-lede mt-6 max-w-lg">Our team built and sold an ERP business. BrandedMark holds what ERP does not: owners, dealers and service after the sale.</p>
      </div>
      <dl className="border-t border-bm-line" data-reveal>
        {founders.credentials.map((c) => (
          <div key={c.k} className="grid grid-cols-[88px_1fr] items-baseline gap-5 border-b border-bm-line py-6">
            <dt className="text-[14.5px] font-semibold text-bm-emerald">{c.k}</dt>
            <dd className="text-[clamp(18px,1.7vw,22px)] font-semibold leading-snug tracking-[-0.015em] text-bm-indigo">{c.v}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
}

export default function Integrations() {
  usePageMeta(
    'ERP integration for aftersales and product registration: Epicor Kinetic, IFS, SAP',
    'Your ERP stays the master. A native Epicor Kinetic connector, IFS Cloud and SAP through our API, and CSV, REST API and webhooks for everything else. Built for GDPR, no middleware to install.'
  );
  useJsonLd('bc-integrations', breadcrumbJsonLd([{ name: 'Home', path: '/' }, { name: 'Integrations', path: '/integrations' }]));

  return (
    <div className="it">
      <KitStyles />
      <style>{`
.v2 .it-big { font-weight: 800; color: var(--bm-indigo); font-size: clamp(52px, 6.4vw, 92px); line-height: .95; letter-spacing: -0.05em; }
`}</style>
      <PageHero
        kicker="ERP integrations"
        crumbs={[
          { label: 'Home', href: '/' },
          { label: 'Integrations', href: '/integrations' },
        ]}
        title={
          <>
            Your ERP stays <Swipe draw>the master.</Swipe>
          </>
        }
        lede={erp.lede}
        aside={<ErpBoard />}
      >
        <Button href={cta.primaryHref}>
          {cta.primary} <Arrow />
        </Button>
        <SmartLink href="/trust/" className="pk-textlink text-[15px] sm:ml-2">
          or read the Trust Centre
        </SmartLink>
      </PageHero>

      <Section id="flows" alt>
        <div className="mx-auto max-w-3xl text-center" data-reveal>
          <TwoTone lead="Nothing is typed twice." />
        </div>
        <div className="mt-14">
          <Flows />
        </div>
      </Section>

      <Section>
        <Proof />
      </Section>

      <Section alt>
        <Team />
      </Section>

      <Section id="other">
        <div className="grid gap-10 lg:grid-cols-[0.8fr_1.2fr] lg:gap-16">
          <div data-reveal>
            <TwoTone lead="Beyond the ERP." />
            <p className="mt-6 max-w-sm text-[16px] text-bm-muted">
              Your own accounts, under your own contracts. Data goes where you send it.
            </p>
          </div>
          <div className="border-t border-bm-line" data-reveal>
            {OTHER.map((o) => (
              <div key={o.t} className="grid gap-2 border-b border-bm-line py-5 sm:grid-cols-[210px_1fr_auto] sm:items-baseline sm:gap-6">
                <div className="text-[17px] font-semibold text-bm-indigo">{o.t}</div>
                <p className="text-[15px] text-bm-muted">{o.d}</p>
                <div>
                  <StatusChip status={o.status} />
                </div>
              </div>
            ))}
          </div>
        </div>
      </Section>

      <Section id="it" alt>
        <Faq faqs={FAQS} id="faq-integrations" title="What your IT team will ask." />
        <div className="mt-8">
          <SmartLink href="/trust/" className="pk-textlink">
            Security and sub-processors in the Trust Centre <Arrow />
          </SmartLink>
        </div>
      </Section>

      <CtaBand
        source="integrations"
        title={['Bring your ERP questions.', 'We built ERP.']}
        lede="Tell us which ERP and version you run. We will tell you how it connects and how long it takes."
      />
    </div>
  );
}
